import React, { useContext } from 'react';
import { Outlet, useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Home,
  LocalLibrary,
  QrCode2,
  MonetizationOn,
  Person,
  AccountBalanceWallet,
  Notifications,
  CardMembership,
  BarChart,
  SelfImprovement,
  School,
} from '@mui/icons-material';
import { AnimatePresence, motion } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import { AppBarProvider, useAppBarState } from '../context/AppBarContext';
import { checkPortalAccess } from '../utils/roleRedirect';
import StudentBottomNav from '../components/StudentBottomNav';
import StudentAppBar from '../components/shared/StudentAppBar';
import ActiveSessionBanner from '../components/ActiveSessionBanner';
import GradientSurface from '../components/shared/GradientSurface';
import BrandMark from '../components/shared/BrandMark';
import { pageVariants, pageTransition } from '../theme/motion';
import { RADIUS } from '../theme/tokens';

const MotionBox = motion(Box);

const DRAWER_WIDTH = 280;

function PersistentAppBarSlot() {
  const config = useAppBarState();
  if (!config) return null;
  return <StudentAppBar {...config} />;
}

const SIDEBAR_ITEMS = [
  { label: 'Home', icon: Home, path: '/student' },
  { label: 'Libraries', icon: LocalLibrary, path: '/student/libraries' },
  { label: 'Access & QR', icon: QrCode2, path: '/student/access' },
  { label: 'Wallet', icon: AccountBalanceWallet, path: '/student/wallet' },
  { label: 'Coins', icon: MonetizationOn, path: '/student/coins' },
  { label: 'Subscriptions', icon: CardMembership, path: '/student/subscriptions' },
  { label: 'Statistics', icon: BarChart, path: '/student/statistics' },
  { label: 'Focus Mode', icon: SelfImprovement, path: '/student/focus-mode' },
  { label: 'Institutes', icon: School, path: '/student/institutes' },
  { label: 'Notifications', icon: Notifications, path: '/student/notifications' },
  { label: 'Profile', icon: Person, path: '/student/profile' },
];

function isPathActive(itemPath, currentPath) {
  if (itemPath === '/student') return currentPath === '/student' || currentPath === '/student/';
  return currentPath.startsWith(itemPath);
}

/**
 * StudentPortal — router-driven shell.
 * Provides desktop left rail + mobile bottom nav + persistent ActiveSessionBanner.
 * All /student/* routes render inside <Outlet />.
 * Exception: /student/scanner renders edge-to-edge (no chrome) so the camera fills the viewport.
 */
function StudentPortal() {
  const { isAuthenticated, user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Hide bottom nav and sticky banner for the full-screen scanner route
  const isScannerRoute = location.pathname === '/student/scanner';

  if (isAuthenticated && user && user.role !== 'student') {
    checkPortalAccess(user, 'student', navigate);
    return null;
  }

  if (isScannerRoute) {
    return <Outlet />;
  }

  return (
    <AppBarProvider>
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'transparent' }}>
      {/* Desktop left rail */}
      {isDesktop && (
        <GradientSurface
          variant="surface"
          radius={0}
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            borderRight: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            overflowY: 'auto',
            zIndex: theme.zIndex.drawer,
          }}
        >
          {/* Brand */}
          <Box sx={{ p: 3, pb: 2 }}>
            <BrandMark size={36} variant="full" />
          </Box>

          {/* User */}
          {user && (
            <Box sx={{ px: 2, pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: 'primary.main',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                  }}
                >
                  {user.name?.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} noWrap color="text.primary">
                    {user.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {user.email}
                  </Typography>
                </Box>
              </Box>
              <Divider />
            </Box>
          )}

          {/* Nav items */}
          <List sx={{ px: 1.5, flex: 1 }}>
            {SIDEBAR_ITEMS.map((item) => {
              const active = isPathActive(item.path, location.pathname);
              const IconComponent = item.icon;
              return (
                <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    selected={active}
                    onClick={() => navigate(item.path)}
                    sx={{
                      borderRadius: RADIUS.md,
                      py: 1.25,
                      '&.Mui-selected': {
                        bgcolor: 'primary.soft',
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'primary.soft' },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: active ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      <IconComponent sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: active ? 700 : 500,
                        color: active ? 'primary.main' : 'text.primary',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </GradientSurface>
      )}

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          ml: isDesktop ? `${DRAWER_WIDTH}px` : 0,
          pb: isDesktop ? 0 : 'calc(72px + env(safe-area-inset-bottom))',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Persistent AppBar — mobile only (responsive display lives inside StudentAppBar);
            mounted directly under <main> so its sticky containing block spans the full scroll area. */}
        <PersistentAppBarSlot />

        {/* Persistent active session banner — compact sticky strip */}
        <ActiveSessionBanner compact sticky />

        {/* Page content with transition */}
        <AnimatePresence mode="wait">
          <MotionBox
            key={location.pathname}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={pageTransition}
            style={{ flex: 1 }}
          >
            <Outlet />
          </MotionBox>
        </AnimatePresence>
      </Box>

      {/* Mobile bottom nav */}
      <StudentBottomNav />
    </Box>
    </AppBarProvider>
  );
}

export default StudentPortal;
